/**
 * Abfrageschlüssel und Mutations-Ungültigkeit (Vertrag §10).
 *
 * Frei getippte Schlüssel sind der häufigste Weg zu einer Liste, die sich
 * nach dem Speichern nicht aktualisiert.
 */

import type { AufgabenFilter } from "./filter.js";
import { sucheAusFilter } from "./filter.js";

const WURZEL = ["cubic"] as const;

function stabil(filter: AufgabenFilter): string {
  return JSON.stringify(sucheAusFilter(filter));
}

export const schluessel = {
  ich: () => [...WURZEL, "ich"] as const,
  projekte: (archivierte = false) =>
    [...WURZEL, "projekte", { archivierte }] as const,
  projekt: (id: string) => [...WURZEL, "projekt", id] as const,
  projektVerzeichnis: (id: string) =>
    [...WURZEL, "projekt", id, "verzeichnis"] as const,
  aufgaben: (projektId: string, filter: AufgabenFilter) =>
    [...WURZEL, "aufgaben", projektId, stabil(filter)] as const,
  /**
   * Aufgaben über mehrere Projekte.
   *
   * **Die Projekte gehören in den Schlüssel.** Vorher bestand er allein aus
   * dem Filter — mit `leererFilter()` also aus einer Konstanten. Fünf Stellen
   * teilten sich dadurch einen einzigen Zwischenspeichereintrag: die
   * Einstiegsseite mit *allen* Projekten sowie Meilenstein-, Arbeitspaket- und
   * Projektübersicht mit je *einem*. Wer den Kontext wechselte, sah bis zu
   * dreißig Sekunden die Zahlen des vorigen Ausschnitts unter der neuen
   * Überschrift — und weil fremde Zustands-IDs aus der Zuordnung fallen,
   * standen die Zeilen dabei alle auf „Offen".
   *
   * Sortiert, damit die Reihenfolge der Projekte keinen zweiten Eintrag
   * erzeugt. Ohne `projektIds` bleibt der kürzere Schlüssel — er ist das
   * Präfix aller langen und taugt weiter zum Invalidieren.
   */
  aufgabenAlle: (filter: AufgabenFilter, projektIds?: readonly string[]) =>
    projektIds
      ? ([
          ...WURZEL,
          "aufgaben",
          "alle",
          stabil(filter),
          [...projektIds].sort().join(","),
        ] as const)
      : ([...WURZEL, "aufgaben", "alle", stabil(filter)] as const),
  aufgabe: (id: string) => [...WURZEL, "aufgabe", id] as const,
  zustaende: (projektId: string) =>
    [...WURZEL, "zustaende", projektId] as const,
  labels: (projektId: string) => [...WURZEL, "labels", projektId] as const,
  meilensteine: (projektId: string) =>
    [...WURZEL, "meilensteine", projektId] as const,
  meilensteinFortschritt: (id: string) =>
    [...WURZEL, "meilenstein", id, "fortschritt"] as const,
  arbeitspakete: (projektId: string, meilensteinId?: string) =>
    [
      ...WURZEL,
      "arbeitspakete",
      projektId,
      meilensteinId ?? "alle",
    ] as const,
  arbeitspaket: (id: string) => [...WURZEL, "arbeitspaket", id] as const,
  arbeitspaketFortschritt: (id: string) =>
    [...WURZEL, "arbeitspaket", id, "fortschritt"] as const,
  abhaengigkeiten: (art: string, id: string) =>
    [...WURZEL, "abhaengigkeiten", art, id] as const,
  /**
   * Eine Einstellung aus der Registry. Bereich **und** Bereichskennung gehören
   * in den Schlüssel: Derselbe Schlüssel meint im Projekt A etwas anderes als
   * im Projekt B.
   */
  einstellung: (
    einstellungsSchluessel: string,
    bereich: string,
    bereichId = "",
  ) =>
    [...WURZEL, "einstellung", einstellungsSchluessel, bereich, bereichId] as const,
  personen: () => [...WURZEL, "personen"] as const,
  person: (id: string) => [...WURZEL, "person", id] as const,
  navigationszahlen: (projektId?: string) =>
    [...WURZEL, "navigation", "zaehler", projektId ?? "alle"] as const,
  seitenBaum: (projektId: string) =>
    [...WURZEL, "seiten", "baum", projektId] as const,
  seitenGlobal: (filter?: { suche?: string }) =>
    [...WURZEL, "seiten", "global", filter ?? {}] as const,
  seite: (seiteId: string) => [...WURZEL, "seiten", "seite", seiteId] as const,
  seitenMarken: (seiteId: string) =>
    [...WURZEL, "seiten", "marken", seiteId] as const,
  seitenFassungen: (seiteId: string) =>
    [...WURZEL, "seiten", "fassungen", seiteId] as const,
  seitenVersionierungsstatus: (seiteId: string) =>
    [...WURZEL, "seiten", "version", seiteId] as const,
  verlauf: (projektId: string, filter: object = {}) =>
    [...WURZEL, "verlauf", projektId, JSON.stringify(filter)] as const,
  klaerungen: (projektId: string, zustand?: string) =>
    [...WURZEL, "klaerungen", projektId, zustand ?? "alle"] as const,
  klaerungenOffen: (projektId: string) =>
    [...WURZEL, "klaerungen", "offen", projektId] as const,
  gitlabMrs: (projektId: string) =>
    [...WURZEL, "gitlab", "mrs", projektId] as const,
  /** Ohne Projekt: die offenen Merge Requests aller sichtbaren Projekte. */
  gitlabOffeneMrs: () => [...WURZEL, "gitlab", "mrs", "offen"] as const,
  gitlabPipelines: (projektId: string) =>
    [...WURZEL, "gitlab", "pipelines", projektId] as const,
  gitlabCommits: (projektId: string) =>
    [...WURZEL, "gitlab", "commits", projektId] as const,
  gitlabAuslieferungen: (projektId: string) =>
    [...WURZEL, "gitlab", "auslieferungen", projektId] as const,
  gitlabUnzugeordnete: (projektId: string) =>
    [...WURZEL, "gitlab", "unzugeordnete", projektId] as const,
  gitlabEinrichtung: (projektId: string) =>
    [...WURZEL, "gitlab", "einrichtung", projektId] as const,
  gitlabProjekteSuchen: (projektId: string) =>
    [...WURZEL, "gitlab", "projekte-suchen", projektId] as const,
  gitlabProjekteVerknuepft: () =>
    [...WURZEL, "gitlab", "projekte-verknuepft"] as const,
  gitlabMrCommits: (projektId: string, mrId: string) =>
    [...WURZEL, "gitlab", "mr", projektId, mrId, "commits"] as const,
  gitlabMrAenderungen: (projektId: string, mrId: string) =>
    [...WURZEL, "gitlab", "mr", projektId, mrId, "aenderungen"] as const,
  gitlabJobTrace: (projektId: string, jobId: number) =>
    [...WURZEL, "gitlab", "trace", projektId, jobId] as const,
  qualitaetsGateRegel: (projektId: string) =>
    [...WURZEL, "qualitaets-gate", "regel", projektId] as const,
  qualitaetsGateStaende: () =>
    [...WURZEL, "qualitaets-gate", "stand"] as const,
  qualitaetsGateStand: (mrId: string) =>
    [...WURZEL, "qualitaets-gate", "stand", mrId] as const,
  autoQaAutomationen: (projektId: string) =>
    [...WURZEL, "auto-qa", "automationen", projektId] as const,
  durchsichtLaeufe: (projektId: string) =>
    [...WURZEL, "durchsicht", "laeufe", projektId] as const,
  durchsichtBefunde: (projektId: string) =>
    [...WURZEL, "durchsicht", "befunde", projektId] as const,
  metarrCodeArbeitspaketOptionen: (projektId: string) =>
    [...WURZEL, "metarr", "arbeitspakete", projektId, "optionen"] as const,
  metarrCodeArbeitspakete: (projektId: string) =>
    [...WURZEL, "metarr", "arbeitspakete", projektId] as const,
  metarrCodeArbeitspaket: (arbeitspaketId: string) =>
    [...WURZEL, "metarr", "arbeitspaket", arbeitspaketId] as const,
} as const;

export type MutationArt =
  | "aufgabe_anlegen"
  | "aufgabe_aendern"
  | "aufgabe_status"
  | "aufgabe_zuweisen"
  | "aufgabe_archivieren"
  | "projekt_anlegen"
  | "projekt_aendern"
  | "meilenstein_anlegen";

/**
 * Welche Abfragen nach einer Mutation ungültig werden.
 * Zustandswechsel berührt Liste, Detail und Meilensteinfortschritt.
 */
export function ungueltigNach(
  art: MutationArt,
  kontext: {
    projektId?: string;
    aufgabeId?: string;
    meilensteinId?: string | null;
    filter?: AufgabenFilter;
  },
): readonly (readonly unknown[])[] {
  const list: (readonly unknown[])[] = [];
  const { projektId, aufgabeId, meilensteinId, filter } = kontext;
  const f = filter;

  const pushAufgaben = () => {
    if (projektId && f) list.push(schluessel.aufgaben(projektId, f));
    if (f) list.push(schluessel.aufgabenAlle(f));
    // Präfix-Invalidierung: auch ohne konkreten Filter die Listen
    if (projektId) list.push([...WURZEL, "aufgaben", projektId]);
    list.push([...WURZEL, "aufgaben", "alle"]);
  };

  switch (art) {
    case "aufgabe_anlegen":
    case "aufgabe_aendern":
    case "aufgabe_zuweisen":
    case "aufgabe_archivieren":
    case "aufgabe_status":
      pushAufgaben();
      if (aufgabeId) list.push(schluessel.aufgabe(aufgabeId));
      if (projektId) {
        list.push(schluessel.projektVerzeichnis(projektId));
        list.push(schluessel.projekte());
      }
      if (meilensteinId)
        list.push(schluessel.meilensteinFortschritt(meilensteinId));
      break;
    case "projekt_anlegen":
    case "projekt_aendern":
      list.push(schluessel.projekte());
      if (projektId) list.push(schluessel.projekt(projektId));
      list.push(schluessel.ich());
      break;
    case "meilenstein_anlegen":
      if (projektId) {
        list.push(schluessel.meilensteine(projektId));
        list.push(schluessel.projektVerzeichnis(projektId));
      }
      break;
  }
  return list;
}
